import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TARGETS = [
  { value: "year_1", label: "Aula I anno" },
  { value: "year_2", label: "Aula II anno" },
  { value: "year_3", label: "Aula III anno" },
  { value: "course", label: "Studenti di un corso" },
  { value: "all_active", label: "Tutti gli studenti attivi" },
];

export default function BroadcastDialog({ open, onOpenChange, onSent }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [target, setTarget] = useState("year_1");
  const [courseId, setCourseId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const { data: courses = [] } = useQuery({
    queryKey: ["courses-broadcast"],
    queryFn: async () => (await api.get("/courses", { params: { is_seminar: false } })).data,
    enabled: open,
  });

  const availableTargets = TARGETS.filter((t) => {
    if (user?.role === "docente") return t.value === "course";
    if (user?.role === "admin" || user?.role === "direzione") return true;
    return t.value !== "all_active";
  });

  const mut = useMutation({
    mutationFn: async () => (await api.post("/threads/broadcast", {
      subject, body, target, course_id: target === "course" ? courseId : undefined,
    })).data,
    onSuccess: (d) => {
      toast.success(`Invio riuscito a ${d.delivered} studenti`);
      qc.invalidateQueries({ queryKey: ["threads"] });
      setSubject(""); setBody(""); setCourseId("");
      onSent?.(d);
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore invio"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white">
        <DialogHeader>
          <DialogTitle>Nuova comunicazione broadcast</DialogTitle>
          <DialogDescription>Crea una conversazione individuale con ogni studente del gruppo selezionato.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (!subject || !body) return; mut.mutate(); }} className="space-y-4">
          <div>
            <Label className="text-xs">Destinatari</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="bc-target"><SelectValue/></SelectTrigger>
              <SelectContent>
                {availableTargets.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {target === "course" && (
            <div>
              <Label className="text-xs">Corso</Label>
              <Select value={courseId} onValueChange={setCourseId}>
                <SelectTrigger data-testid="bc-course"><SelectValue placeholder="Seleziona…"/></SelectTrigger>
                <SelectContent>
                  {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.year_level ? `${c.year_level}° anno` : "—"})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Oggetto</Label>
            <Input data-testid="bc-subject" value={subject} onChange={(e) => setSubject(e.target.value)} required placeholder="es. Riunione plenaria mercoledì"/>
          </div>
          <div>
            <Label className="text-xs">Messaggio</Label>
            <Textarea rows={5} data-testid="bc-body" value={body} onChange={(e) => setBody(e.target.value)} required placeholder="Scrivi il testo del comunicato…"/>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={mut.isPending || (target === "course" && !courseId)} data-testid="bc-send"
              className="bg-[#E2725B] hover:bg-[#c85d47]">
              {mut.isPending ? "Invio…" : "Invia broadcast"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
